// src/pages/SendDonation.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { ReactComponent as Trash } from "../assets/images/trash.svg";
import { createHttpClient } from "../api/client";

function SuccessIcon() {
  return (
    <div className="h-14 w-14 rounded-full bg-emerald-600 grid place-items-center">
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
        <path
          d="M20 6L9 17l-5-5"
          stroke="white"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

const NUM = new Intl.NumberFormat("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const INT = new Intl.NumberFormat("th-TH", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

function uid(prefix = "id") {
  return `${prefix}_${Math.random().toString(16).slice(2)}_${Date.now()}`;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toYMD(date) {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

// ให้ได้ "YYYY-MM-DDTHH:mm:ss" (ไม่มี Z ก็ได้)
function normalizeDateTimeParam(raw, end = false) {
  if (!raw) return "";
  const s = String(raw).trim();
  if (!s) return "";

  // ถ้ามี T อยู่แล้ว ส่งตามนั้นเลย
  if (s.includes("T")) return s.replace(".000Z", "Z");

  // ถ้าเป็น YYYY-MM-DD เติมเวลาให้
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return `${s}T${end ? "23:59:00" : "00:00:00"}`;
  }

  // เผื่อเป็น date string อื่นๆ
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const ymd = toYMD(d);
  return `${ymd}T${end ? "23:59:00" : "00:00:00"}`;
}

function readStoredProfile() {
  const keys = ["profile", "PROFILE", "user_profile", "USER_PROFILE"];
  for (const k of keys) {
    const raw = localStorage.getItem(k);
    if (!raw) continue;
    try {
      const obj = JSON.parse(raw);
      if (obj?.user) return obj;
    } catch { }
  }
  return null;
}

export default function SendDonation() {
  const nav = useNavigate();
  const location = useLocation();
  const http = useMemo(() => createHttpClient(), []);

  const sp = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const startParamRaw = sp.get("start_date") || sp.get("startdate") || "";
  const endParamRaw = sp.get("end_date") || sp.get("enddate") || "";

  const startDT = useMemo(() => normalizeDateTimeParam(startParamRaw, false), [startParamRaw]);
  const endDT = useMemo(() => normalizeDateTimeParam(endParamRaw, true), [endParamRaw]);

  // ใช้ start_date เป็น distribute_date (YYYY-MM-DD)
  const distributeDate = useMemo(() => {
    const base = startDT || startParamRaw;
    const d = base ? new Date(base) : null;
    return d && !Number.isNaN(d.getTime()) ? toYMD(d) : "";
  }, [startDT, startParamRaw]);

  const [step, setStep] = useState(1);
  const [pageLoading, setPageLoading] = useState(true);

  // ✅ profile
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState("");
  const [userId, setUserId] = useState(null); // ใช้ยิง getDonation/user/:id และใช้เป็น distribute_by
  const [profileProvince, setProfileProvince] = useState("");

  // ✅ donation จากช่วงวันที่
  const [donationLoading, setDonationLoading] = useState(false);
  const [donationError, setDonationError] = useState("");
  const [donationId, setDonationId] = useState(null);
  const [donationRows, setDonationRows] = useState([]); // [{name, qty}]
  const [donationTotalKg, setDonationTotalKg] = useState(0); // ✅ รวม weight จาก donation

  // ✅ remaining จาก distribution (null = ยังไม่มีข้อมูล -> fallback ไป donationTotalKg)
  const [remainingFromDistribution, setRemainingFromDistribution] = useState(null);

  // ✅ communities from API
  const [communities, setCommunities] = useState([]);
  const [communityLoading, setCommunityLoading] = useState(true);
  const [communityError, setCommunityError] = useState("");

  // ✅ Step1 recipients (unique communityId)
  const [recipients, setRecipients] = useState([{ id: uid("rcp"), communityId: "", people: "0" }]);

  // ✅ Step2 kg
  const [kgByRecipientId, setKgByRecipientId] = useState({}); // { [id]: "0.00" }

  const [successOpen, setSuccessOpen] = useState(false);

  // ✅ remaining จาก distribution
  const [remainingLoading, setRemainingLoading] = useState(false);
  const [remainingError, setRemainingError] = useState("");

  // small initial overlay
  useEffect(() => {
    const t = setTimeout(() => setPageLoading(false), 250);
    return () => clearTimeout(t);
  }, []);

  // ✅ ยอดคงเหลือฐาน: ถ้า distribution ไม่มีข้อมูล -> ใช้ยอดรวมจาก donation
  const remainingBaseKg = remainingFromDistribution === null ? donationTotalKg : remainingFromDistribution;

  // --- load remaining from distributions (use first item only) ---
  useEffect(() => {
    let alive = true;

    (async () => {
      setRemainingError("");
      setRemainingFromDistribution(null); // ✅ default = ไม่มีข้อมูล -> fallback

      if (!userId) return;
      if (!startDT || !endDT) return;

      setRemainingLoading(true);
      try {
        const res = await http.get(`/api/distribution/getByUserIdDistributions/${userId}`, {
          params: { start_date: startDT, end_date: endDT, page: 1, limit: 10 },
        });

        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        const usable = list.filter((x) => !x?.is_delete);

        const first = usable[0] || null; // ✅ ใช้ index แรกเท่านั้น
        const remaining = first ? Number(first?.remaining_weight || 0) : null; // ✅ ไม่มี -> null

        if (!alive) return;
        setRemainingFromDistribution(remaining === null || !Number.isFinite(remaining) ? null : remaining);
      } catch (e) {
        console.error("GET /api/distribution/getByUserIdDistributions/:id error:", e);
        if (!alive) return;
        setRemainingError("โหลดยอดคงเหลือไม่สำเร็จ");
        setRemainingFromDistribution(null); // ✅ error ก็ fallback
      } finally {
        if (alive) setRemainingLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [http, userId, startDT, endDT]);

  // --- load profile (user id) ---
  useEffect(() => {
    let alive = true;

    (async () => {
      setProfileLoading(true);
      setProfileError("");

      try {
        const res = await http.get("/api/auth/profile");
        const u = res?.data?.user;
        const addr0 = Array.isArray(u?.addresses) ? u.addresses[0] : null;
        const provinceId = String(addr0?.province_id || "").trim();
        if (!u?.id) throw new Error("missing user.id");
        if (!alive) return;

        setUserId(Number(u.id));
        setProfileProvince(provinceId);
        localStorage.setItem("profile", JSON.stringify(res.data));
      } catch (e) {
        console.error("GET /api/auth/profile error:", e);

        const cached = readStoredProfile();
        const id = cached?.user?.id;
        const addr0 = Array.isArray(cached?.user?.addresses) ? cached.user.addresses[0] : null;
        const provinceId = String(addr0?.province_id || "").trim();

        if (!alive) return;

        if (id) {
          setUserId(Number(id));
          setProfileProvince(provinceId);
          setProfileError("");
        } else {
          setProfileError("โหลดข้อมูลผู้ใช้งานไม่สำเร็จ");
          setUserId(null);
          setProfileProvince("");
        }
      } finally {
        if (alive) setProfileLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [http]);

  // --- load communities ---
  useEffect(() => {
    let alive = true;

    (async () => {
      setCommunityLoading(true);
      setCommunityError("");

      const provinceLabel =
        profileProvince === "29"
          ? "อุดรธานี"
          : profileProvince === "10"
            ? "สระบุรี"
            : "";

      if (!provinceLabel) {
        if (!alive) return;
        setCommunities([]);
        setCommunityLoading(false);
        return;
      }

      try {
        const res = await http.get("/api/communitie/getAllCommunitie", {
          params: {
            province: provinceLabel,
          },
        });
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        const usable = list.filter((x) => x?.is_active && !x?.is_delete);

        if (!alive) return;
        setCommunities(usable);
      } catch (e) {
        console.error("GET /api/communitie/getAllCommunitie error:", e);
        if (!alive) return;
        setCommunityError("โหลดรายชื่อชุมชนไม่สำเร็จ");
        setCommunities([]);
      } finally {
        if (alive) setCommunityLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [http, profileProvince]);

  // --- load donation by date range ---
  useEffect(() => {
    let alive = true;

    (async () => {
      setDonationError("");
      setDonationId(null);
      setDonationRows([]);
      setDonationTotalKg(0); // ✅ reset fallback total

      if (!userId) return;
      if (!startDT || !endDT) return; // ต้องมี params มาก่อน

      setDonationLoading(true);

      try {
        const url =
          `/api/donations/getDonation/user/${userId}` +
          `?start_date=${encodeURIComponent(startDT)}` +
          `&end_date=${encodeURIComponent(endDT)}` +
          `&page=1&limit=10`;

        const res = await http.get(url);
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];

        const drafts = list.filter((x) => !x?.is_delete && x?.status === "draft");
        const usable = drafts.length ? drafts : list.filter((x) => !x?.is_delete);

        if (!alive) return;

        if (!usable.length) {
          setDonationError("ไม่พบรายการบริจาคในช่วงวันที่ที่เลือก");
          setDonationId(null);
          setDonationRows([]);
          setDonationTotalKg(0);
          return;
        }

        const picked = usable[0];
        setDonationId(Number(picked.id));

        let totalByTotalWeight = 0;
        for (const d of usable) {
          const tw = Number(d?.total_weight ?? 0);
          if (Number.isFinite(tw) && tw > 0) totalByTotalWeight += tw;
        }

        const allItems = usable.flatMap((d) => (Array.isArray(d?.items) ? d.items : []));

        const bucket = new Map();
        let totalByItems = 0;

        for (const it of allItems) {
          const name = String(it?.food_type_name || it?.food_name || "-");
          const w = Number(it?.weight || 0);
          if (!name || !Number.isFinite(w) || w <= 0) continue;

          totalByItems += w; // เผื่อ fallback
          bucket.set(name, (bucket.get(name) || 0) + w);
        }

        const rows = Array.from(bucket.entries())
          .map(([name, qty]) => ({ name, qty }))
          .sort((a, b) => b.qty - a.qty);

        setDonationRows(rows);
        // ✅ ใช้ total_weight รวมเป็นหลัก (ถ้าไม่มี/เป็น 0 ค่อย fallback ไป sum จาก items)
        setDonationTotalKg(totalByTotalWeight > 0 ? totalByTotalWeight : totalByItems);
      } catch (e) {
        console.error("GET /api/donations/getDonation/user/:id error:", e);
        if (!alive) return;
        setDonationError("โหลดรายการอาหารบริจาคไม่สำเร็จ");
        setDonationId(null);
        setDonationRows([]);
        setDonationTotalKg(0);
      } finally {
        if (alive) setDonationLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [http, userId, startDT, endDT]);

  // --- community helpers ---
  const COMMUNITY_OPTIONS = useMemo(() => {
    return [
      { value: "", label: "กรุณาเลือกชุมชน" },
      ...communities.map((c) => ({ value: String(c.id), label: String(c.name || "").trim() })),
    ];
  }, [communities]);

  function getCommunityNameById(id) {
    const hit = communities.find((x) => String(x.id) === String(id));
    return hit?.name ? String(hit.name).trim() : "";
  }

  const selectedCommunitySet = useMemo(() => {
    const s = new Set();
    recipients.forEach((r) => {
      const v = String(r.communityId || "").trim();
      if (v) s.add(v);
    });
    return s;
  }, [recipients]);

  // --- Step1 validations (รวม unique) ---
  const totalPeople = useMemo(() => {
    return recipients.reduce((s, r) => s + (Number(r.people) || 0), 0);
  }, [recipients]);

  const hasDuplicateCommunities = useMemo(() => {
    const ids = recipients.map((r) => String(r.communityId || "").trim()).filter(Boolean);
    return new Set(ids).size !== ids.length;
  }, [recipients]);

  const step1Valid = useMemo(() => {
    const communityOk = recipients.every((r) => String(r.communityId || "").trim());
    const peopleOk = recipients.every((r) => Number(r.people) >= 0 && Number.isFinite(Number(r.people)));
    const apiOk = !communityLoading && !communityError;

    // ✅ ต้องมีประชากรรวมมากกว่า 0 ถึงไป step2 ได้
    const totalOk = (Number(totalPeople) || 0) > 0;

    return communityOk && peopleOk && apiOk && !hasDuplicateCommunities && totalOk;
  }, [recipients, communityLoading, communityError, hasDuplicateCommunities, totalPeople]);

  // --- Step2 calculations + validations ---
  const recipientsForStep2 = useMemo(() => {
    return recipients.filter((r) => String(r.communityId || "").trim());
  }, [recipients]);

  const totalRecipientKg = useMemo(() => {
    return recipientsForStep2.reduce((s, r) => s + (Number(kgByRecipientId[r.id]) || 0), 0);
  }, [recipientsForStep2, kgByRecipientId]);

  // ✅ ใช้ remainingBaseKg (fallback)
  const remainingAfter = useMemo(
    () => Math.max(0, remainingBaseKg - totalRecipientKg),
    [remainingBaseKg, totalRecipientKg]
  );
  const withinRemaining = totalRecipientKg <= remainingBaseKg;

  const eachKgMinOk = useMemo(() => {
    return recipientsForStep2.every((r) => (Number(kgByRecipientId[r.id]) || 0) > 0.1);
  }, [recipientsForStep2, kgByRecipientId]);

  const step2Valid = withinRemaining && eachKgMinOk && !!donationId && donationRows.length > 0;

  // --- actions ---
  function goBack() {
    if (step === 1) nav(-1);
    else setStep(1);
  }

  const canAddRecipient = useMemo(() => {
    // ถ้าใช้ครบทุกชุมชนแล้ว ไม่ให้เพิ่ม
    const usable = communities.length;
    const used = selectedCommunitySet.size;
    return usable === 0 ? true : used < usable;
  }, [communities.length, selectedCommunitySet.size]);

  function addRecipient() {
    if (!canAddRecipient) return;
    setRecipients((prev) => [...prev, { id: uid("rcp"), communityId: "", people: "0" }]);
  }

  function handleCommunityChange(rowId, nextVal) {
    const v = String(nextVal || "").trim();
    if (!v) {
      setRecipients((prev) => prev.map((x) => (x.id === rowId ? { ...x, communityId: "" } : x)));
      return;
    }

    // กันซ้ำแบบชัวร์: ถ้าคนอื่นเลือกแล้ว ห้ามตั้ง
    const isDup = recipients.some((r) => r.id !== rowId && String(r.communityId || "") === v);
    if (isDup) return;

    setRecipients((prev) => prev.map((x) => (x.id === rowId ? { ...x, communityId: v } : x)));
  }

  function updateRecipient(id, patch) {
    setRecipients((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }

  function deleteRecipient(id) {
    setRecipients((prev) => {
      const next = prev.filter((x) => x.id !== id);

      setKgByRecipientId((m) => {
        const copy = { ...m };
        delete copy[id];
        return copy;
      });

      return next.length === 0 ? [{ id: uid("rcp"), communityId: "", people: "0" }] : next;
    });
  }

  function handleNext() {
    if (!step1Valid) return;

    setKgByRecipientId((prev) => {
      const next = { ...prev };
      recipients
        .filter((r) => String(r.communityId || "").trim())
        .forEach((r) => {
          if (next[r.id] == null) next[r.id] = "";
        });
      return next;
    });



    setStep(2);
  }

  function setKg(id, value) {
    setKgByRecipientId((prev) => ({ ...prev, [id]: value }));
  }

  function toFixed2(n) {
    const x = Math.max(0, Number(n || 0));
    return x.toFixed(2);
  }

  function handleKgChange(recipientId, value) {
    const v = value;

    if (v === "") {
      setKg(recipientId, "");
      return;
    }

    if (!/^\d+(\.\d{0,2})?$/.test(v)) return;

    const n = Number(v);
    if (!Number.isFinite(n)) return;

    if (n > 100000) {
      setKg(recipientId, "100000.00");
      return;
    }

    const otherSum = recipientsForStep2.reduce((s, r) => {
      if (r.id === recipientId) return s;
      return s + (Number(kgByRecipientId[r.id]) || 0);
    }, 0);

    // ✅ ใช้ remainingBaseKg (fallback)
    const quota = Math.max(0, remainingBaseKg - otherSum);

    if (n > quota) {
      setKg(recipientId, toFixed2(quota));
      return;
    }

    if (n < 0.1) return;

    setKg(recipientId, v);
  }

  // ✅ POST distribution/addDistributions
  async function confirmSubmit() {
    if (!step2Valid) return;

    try {
      setDonationLoading(true);

      const receivers = recipientsForStep2.map((r) => ({
        community_id: Number(r.communityId),
        total_people: Number(r.people) || 0,
        distributed_community: Number(kgByRecipientId[r.id]) || 0,
      }));

      const payload = {
        donation_id: Number(donationId),
        distribute_by: Number(userId), // จาก profile (localStorage/endpoint)
        distribute_date: distributeDate, // startdate ที่ส่งมา (YYYY-MM-DD)
        status: "draft",
        receivers,
      };

      await http.post("/api/distribution/addDistributions", payload);

      setSuccessOpen(true);
      setTimeout(() => {
        setSuccessOpen(false);
        nav("/donation_list");
      }, 900);
    } catch (e) {
      console.error("POST /api/distribution/addDistributions error:", e);
      alert("บันทึกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
    } finally {
      setDonationLoading(false);
    }
  }

  const overlayLoading = pageLoading || profileLoading || communityLoading || donationLoading || remainingLoading;

  return (
    <div className="min-h-screen bg-white flex flex-col pb-32 sm:w-full md:max-w-[520px] items-center self-center mx-auto">
      <div className="w-full">
        <Header />
      </div>

      {/* Loading overlay */}
      {overlayLoading && (
        <div className="fixed inset-0 z-[999] bg-white/70 backdrop-blur-[2px] grid place-items-center">
          <div className="flex items-center gap-3 rounded-2xl bg-white border border-slate-200 px-5 py-4 shadow">
            <div className="h-5 w-5 animate-spin rounded-full border border-emerald-600 border-t-transparent" />
            <div className="text-[14px] text-[#4B5563]">กำลังโหลดข้อมูล…</div>
          </div>
        </div>
      )}

      {/* header */}
      <div className="pb-4 w-full px-4 items-start flex flex-col">
        <button
          type="button"
          onClick={goBack}
          aria-label="ย้อนกลับ"
          className="flex items-center gap-2 w-full self-start justify-start text-[#4B5563]"
        >
          <div className="grid place-items-center rounded-full hover:bg-slate-100">
            <span className="text-2xl leading-none">‹</span>
          </div>
          <div className="text-[18px] font-medium">ส่งต่ออาหารบริจาค</div>
        </button>

        <Stepper step={step} />
      </div>

      {(profileError || communityError || donationError || remainingError) && (
        <div className="w-full px-4 space-y-2">
          {profileError && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {profileError}
            </div>
          )}
          {remainingError && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {remainingError}
            </div>
          )}
          {communityError && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {communityError}
            </div>
          )}
          {donationError && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {donationError}
            </div>
          )}
        </div>
      )}

      {/* STEP 1 */}
      {step === 1 ? (
        <div className="pt-4 w-full px-4">
          <div className="flex items-center justify-between">
            <div className="text-lg font-semibold text-slate-900">เพิ่มผู้รับบริจาค</div>
            <button
              type="button"
              onClick={addRecipient}
              disabled={!canAddRecipient}
              className={[
                "rounded-lg px-5 py-2 text-[14px] font-medium shadow-sm active:scale-[0.99]",
                canAddRecipient ? "bg-[#ED9C50] text-white" : "bg-slate-200 text-slate-500 cursor-not-allowed",
              ].join(" ")}
            >
              เพิ่ม
            </button>
          </div>

          {hasDuplicateCommunities && <div className="mt-3 text-[12px] text-rose-600">ชุมชนต้องไม่ซ้ำกัน</div>}

          <div className="mt-4 space-y-3">
            {recipients.map((r) => {
              const communityInvalid = !String(r.communityId || "").trim();
              const peopleNum = Number(r.people);
              const peopleInvalid = !Number.isFinite(peopleNum) || peopleNum < 0;

              // ✅ options ของแถวนี้: เอาที่ถูกเลือกในแถวอื่นออก (แต่คง option ของตัวเองไว้)
              const optionsForRow = COMMUNITY_OPTIONS.filter((opt) => {
                if (!opt.value) return false;
                if (String(opt.value) === String(r.communityId || "")) return true;
                return !selectedCommunitySet.has(String(opt.value));
              });

              return (
                <div key={r.id} className="grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-7">
                    <select
                      className={[
                        "w-full rounded-xl border bg-white px-4 py-3 text-[14px] outline-none",
                        communityInvalid ? "border-red-300" : "border-[#9CA3AF]",
                      ].join(" ")}
                      value={r.communityId}
                      onChange={(e) => handleCommunityChange(r.id, e.target.value)}
                      disabled={communityLoading || !!communityError}
                    >
                      <option value="" disabled>
                        {communityLoading ? "กำลังโหลดชุมชน..." : communityError ? "โหลดชุมชนไม่สำเร็จ" : "กรุณาเลือกชุมชน"}
                      </option>

                      {optionsForRow.map((x) => (
                        <option key={x.value} value={x.value}>
                          {x.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-span-3">
                    <div
                      className={[
                        "w-full rounded-xl border bg-white px-4 py-3 text-[14px] flex items-center justify-between",
                        peopleInvalid ? "border-red-300" : "border-[#9CA3AF]",
                      ].join(" ")}
                    >
                      <input
                        className="w-full outline-none bg-transparent text-center pr-2"
                        type="number"
                        min="0"
                        step="1"
                        value={r.people}
                        onChange={(e) => updateRecipient(r.id, { people: e.target.value })}
                        inputMode="numeric"
                      />
                      <span className="text-slate-40 whitespace-nowrap">คน</span>
                    </div>
                  </div>

                  <div className="col-span-2 flex justify-end">
                    <button
                      type="button"
                      onClick={() => deleteRecipient(r.id)}
                      className="h-12 w-12 rounded-xl bg-[#F7E3E3] grid place-items-center hover:bg-red-100"
                      aria-label="ลบผู้รับบริจาค"
                    >
                      <Trash className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <BottomFixed>
            <div className="w-full max-w-[720px] flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="text-[14px] text-[#4B5563] leading-tight">
                  จำนวนประชากร
                  <br />
                  รวมทั้งหมด
                </div>
                <div className="w-[110px] max-w-[110px] h-11 rounded-xl border border-[#9CA3AF] bg-white px-2 flex items-center gap-1 text-[#4B5563]">
                  <div className="flex-1 min-w-0 truncate text-center">{INT.format(totalPeople)}</div>
                  <span className="shrink-0 font-medium text-slate-40">คน</span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleNext}
                disabled={!step1Valid}
                className={[
                  "w-[150px] h-11 rounded-xl font-medium text-[16px]",
                  step1Valid ? "bg-[#3E6F5E] text-white" : "bg-slate-200 text-slate-500 cursor-not-allowed",
                ].join(" ")}
              >
                ถัดไป
              </button>
            </div>
          </BottomFixed>
        </div>
      ) : (
        /* STEP 2 */
        <div className="pt-4 pb-24 w-full px-4">
          <div className="flex items-center justify-between">
            <div className="text-[16px] font-medium text-slate-900">รายการอาหารบริจาค</div>
            <div className="text-[16px] font-medium text-slate-900">{donationRows.length} รายการ</div>
          </div>

          <div className="mt-3 overflow-hidden rounded-xl border border-[#F3F0DC]">
            <table className="w-full text-left text-[14px]">
              <tbody>
                {donationRows.map((r, idx) => (
                  <tr key={`${r.name}-${idx}`} className="border-t border-[#F3F0DC]">
                    <td className="px-4 py-3 text-slate-800">{r.name}</td>
                    <td className="px-4 py-3 text-right font-medium text-slate-900">
                      {NUM.format(r.qty)} <span className="font-medium text-[#4B5563]">กก.</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* totals */}
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <div className="text-sm text-slate-500 mb-2">น้ำหนักอาหารบริจาคทั้งหมด</div>
              <div className="h-12 rounded-xl border border-[#9CA3AF] bg-[#F5F5F5] flex flex-row items-center justify-between px-10 place-items-center font-medium text-slate-900">
                {NUM.format(donationTotalKg)} <span className="ml-2 font-medium text-[#9CA3AF]">กก.</span>
              </div>
            </div>

            <div>
              <div className="text-sm text-slate-500 mb-2">ยอดคงเหลือ</div>
              <div className="h-12 rounded-xl border border-[#9CA3AF] bg-[#F5F5F5] flex flex-row items-center justify-between px-10 place-items-center font-medium text-[#ED9C50]">
                {NUM.format(remainingAfter)} <span className="ml-2 font-medium text-[#9CA3AF]">กก.</span>
              </div>
            </div>
          </div>

          <div className="mt-5 h-[1px] bg-[#9CA3AF]" />

          <div className="mt-4 text-[16px] font-medium text-slate-900">รายการผู้รับบริจาค</div>

          <div className="mt-3 space-y-3">
            {recipientsForStep2.map((r) => {
              const kgVal = kgByRecipientId[r.id] ?? "";
              const kgNum = Number(kgVal) || 0;
              const kgInvalid = !Number.isFinite(kgNum) || kgNum <= 0.1;

              return (
                <div key={r.id} className="grid grid-cols-12 gap-3 items-center rounded-xl border border-[#9CA3AF] p-2">
                  <div className="col-span-7 min-h-12 bg-white px-4 flex items-center">
                    <div className="text-slate-700 font-medium text-[14px] leading-5 line-clamp-2 break-words">
                      {getCommunityNameById(r.communityId) || "-"}
                    </div>
                  </div>

                  <div className="col-span-5 flex flex-row items-center gap-3">
                    <div
                      className={[
                        "h-12 rounded-xl border bg-white px-4 text-[14px] flex items-center justify-between",
                        kgInvalid ? "border-red-300" : "border-[#9CA3AF]",
                      ].join(" ")}
                    >
                      <input
                        className="w-full outline-none bg-transparent text-center pr-2 font-medium text-slate-900"
                        type="number"
                        min="0.1"
                        max="100000"
                        step="0.1"
                        value={kgVal}
                        inputMode="decimal"
                        onChange={(e) => handleKgChange(r.id, e.target.value)}
                      />
                      <span className="text-[#9CA3AF] whitespace-nowrap">กก.</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <BottomFixed>
            <div className="w-full max-w-[720px] flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="w-1/2 h-12 rounded-xl border border-[#9CA3AF] bg-white py-3 text-[16px] font-medium text-[#4B5563]"
              >
                กลับ
              </button>

              <button
                type="button"
                onClick={confirmSubmit}
                disabled={!step2Valid}
                className={[
                  "w-1/2 h-12 rounded-xl py-3 text-[16px] font-medium",
                  step2Valid ? "bg-[#3E6F5E] text-white" : "bg-slate-200 text-slate-500 cursor-not-allowed",
                ].join(" ")}
              >
                ยืนยัน
              </button>
            </div>

            {!withinRemaining && (
              <div className="mt-2 text-[12px] text-red-500 w-full max-w-[720px] text-right">
                น้ำหนักรวมต้องไม่มากกว่า “จำนวนคงเหลือ” ({NUM.format(remainingBaseKg)} กก.)
              </div>
            )}
            {!donationId && (
              <div className="mt-2 text-[12px] text-red-500 w-full max-w-[720px] text-right">
                ไม่พบ donation ในช่วงวันที่ที่เลือก
              </div>
            )}
          </BottomFixed>
        </div>
      )}

      <Modal open={successOpen}>
        <div className="w-[320px] rounded-3xl bg-white p-6 shadow-xl border border-slate-100 text-center">
          <div className="grid place-items-center">
            <SuccessIcon />
          </div>
          <div className="mt-4 text-[18px] font-medium text-slate-900">ยืนยันการส่งสำเร็จ</div>
          <div className="mt-1 text-[13px] text-slate-500">ระบบกำลังพากลับไปหน้ารายการ</div>
        </div>
      </Modal>
    </div>
  );
}

/* ---------------- UI parts ---------------- */

function Stepper({ step }) {
  const s1Done = step >= 2;
  return (
    <div className="mt-3 flex items-center gap-3 text-[14px]">
      <div className="flex items-center gap-2">
        <Circle active={step === 1} done={s1Done} number="1" />
        <span className={s1Done ? "text-slate-600" : "text-emerald-700 font-medium"}>ข้อมูลผู้รับบริจาค</span>
      </div>

      <div className="h-[2px] w-10 bg-[#9CA3AF] rounded-full" />

      <div className="flex items-center gap-2">
        <Circle active={step === 2} done={false} number="2" />
        <span className={step === 2 ? "text-emerald-700 font-medium" : "text-slate-600"}>รายการอาหารบริจาค</span>
      </div>
    </div>
  );
}

function Circle({ active, done, number }) {
  if (done) return <div className="h-7 w-7 rounded-full bg-[#447E45] text-white grid place-items-center">✓</div>;
  return (
    <div
      className={[
        "h-7 w-7 rounded-full grid place-items-center border font-medium",
        active ? "border-[#84DA8A] text-[#407865]" : "border-[#4B5563] text-[#9CA3AF]",
      ].join(" ")}
    >
      {number}
    </div>
  );
}

function BottomFixed({ children }) {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-[50] bg-white">
      <div className="px-4 sm:px-6 py-4 border-t border-[#9CA3AF] grid place-items-center rounded-t-3xl">
        {children}
      </div>
    </div>
  );
}

function Modal({ open, children }) {
  if (!open) return null;
  return <div className="fixed inset-0 z-[999] grid place-items-center bg-black/30 px-4">{children}</div>;
}
